import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import ReactECharts, { type EChartsOption } from "echarts-for-react";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumericInput } from "@/components/ui/numeric-input";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { SimpleSelect } from "@/components/ui/simple-select";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import { Play, Square, Plus, Zap, Bell, BellOff } from "lucide-react";
import type { AirSensorReading, SensorState } from "./route";
import { BrewLinkDeviceDetail } from "@/features/devices/BrewLinkDeviceDetail";
import { AirSensorExportButton } from "@/features/devices/AirSensorExportButton";
import {
  AIR_SENSOR_METRICS,
  AIR_HISTORY_RANGES,
  type RangeKey,
} from "@/features/devices/air-sensor-constants";

export const Route = createFileRoute("/devices/$deviceId")({
  component: DeviceDetail,
});

interface GatewayDevice {
  id: string;
  device_type: string;
  name: string;
  address: string;
  online: boolean;
  state: {
    // kamoer_pump
    running?: boolean;
    speed?: number;
    tubing_diameter?: number;
    is_active?: boolean;
    last_keepalive?: string;
    consecutive_failures?: number;
    // rtd_node
    sensors?: Record<string, SensorState>;
    // air_sensor
    readings?: Record<string, AirSensorReading>;
    prefix?: string;
    last_tick?: string | null;
    error?: string | null;
    snooze_until?: string | null;
    // Health verdict attached by the gateway's /devices response (air_sensor only).
    health?: {
      status: string;
      message?: string;
      issues?: string[];
    };
  };
  presets?: Array<{ rpm: number; flow_rate: number }>;
}

function DeviceDetail() {
  const { deviceId } = Route.useParams();
  const id = decodeURIComponent(deviceId);

  // Persisted device-registry devices (e.g. BrewLink) are routed as "bb:<id>".
  if (id.startsWith("bb:")) {
    return <BrewLinkDeviceDetail deviceId={Number(id.slice(3))} />;
  }
  return <GatewayDeviceDetail id={id} />;
}

function GatewayDeviceDetail({ id }: { id: string }) {
  const devicesQuery = trpc.devices.list.useQuery(undefined, {
    refetchInterval: 3_000,
  });

  const devices: GatewayDevice[] =
    (devicesQuery.data as { devices?: GatewayDevice[] })?.devices ?? [];
  const device = devices.find((d) => d.id === id);

  if (!device) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-olive-11">
        {devicesQuery.isLoading ? "Loading..." : "Device not found"}
      </div>
    );
  }

  if (device.device_type === "kamoer_pump") {
    return <PumpDetail device={device} />;
  }

  if (device.device_type === "rtd_node") {
    return <RtdNodeDetail device={device} />;
  }

  if (device.device_type === "air_sensor") {
    return <AirSensorDetail device={device} />;
  }

  return (
    <div className="p-4 text-sm text-olive-11">
      Unknown device type: {device.device_type}
    </div>
  );
}

function ClickToEditText({
  value,
  onCommit,
  className,
  inputClassName,
}: {
  value: string;
  onCommit: (value: string) => void;
  className?: string;
  inputClassName?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (!editing) {
    return (
      <button
        className={cn("text-left hover:underline", className)}
        onClick={() => {
          setDraft(value);
          setEditing(true);
        }}
      >
        {value}
      </button>
    );
  }

  return (
    <Input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onCommit(draft);
        if (e.key === "Escape") {
          setEditing(false);
          setDraft(value);
        }
      }}
      onBlur={() => {
        if (draft !== value) onCommit(draft);
        else setEditing(false);
      }}
      className={inputClassName}
      autoFocus
    />
  );
}

function ClickToEditNumeric({
  value,
  onCommit,
  unit,
  placeholder,
}: {
  value: number | null;
  onCommit: (value: number) => void;
  unit?: string;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<number | null>(value);

  if (!editing) {
    return (
      <button
        className="text-left font-mono text-sm hover:underline"
        onClick={() => {
          setDraft(value);
          setEditing(true);
        }}
      >
        {value != null ? (
          <>
            {String(value)}
            {unit && <span className="text-olive-11"> {unit}</span>}
          </>
        ) : (
          <span className="text-olive-11">{placeholder ?? "—"}</span>
        )}
      </button>
    );
  }

  return (
    <NumericInput
      value={draft}
      onValueChange={(v) => setDraft(v ?? null)}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === "Enter" && draft != null) {
          onCommit(draft);
          setEditing(false);
        }
        if (e.key === "Escape") {
          setEditing(false);
          setDraft(value);
        }
      }}
      onBlur={() => {
        if (draft != null && draft !== value) {
          onCommit(draft);
        }
        setEditing(false);
      }}
      className="h-7 w-24 font-mono text-sm"
      autoFocus
    />
  );
}

function PumpDetail({ device }: { device: GatewayDevice }) {
  const utils = trpc.useUtils();
  const isRunning = device.state.running === true;
  const presets = device.presets ?? [];

  const commandMutation = trpc.devices.command.useMutation({
    onSuccess: () => utils.devices.list.invalidate(),
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = trpc.devices.update.useMutation({
    onSuccess: () => utils.devices.list.invalidate(),
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-olive-6 px-4 py-3">
        <div
          className={cn(
            "size-2.5 rounded-full",
            isRunning && "bg-green-9 animate-pulse",
            !isRunning && device.online && "bg-olive-9",
            !device.online && "bg-red-9",
          )}
        />
        <div className="min-w-0 flex-1">
          <ClickToEditText
            value={device.name}
            onCommit={(name) => updateMutation.mutate({ id: device.id, name })}
            className="text-base font-semibold"
            inputClassName="h-7 w-60 text-base font-semibold"
          />
          <div className="flex items-center gap-2 text-xs text-olive-11">
            <span className="font-mono">{device.address}</span>
            <span>
              {isRunning ? "Running" : device.online ? "Stopped" : "Offline"}
            </span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex-1 p-4">
        <div className="flex flex-col gap-4">
          {/* Start / Stop */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                commandMutation.mutate({ id: device.id, command: "start" })
              }
              disabled={commandMutation.isPending}
              className="text-green-11"
            >
              <Play className="size-3.5" />
              Start
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                commandMutation.mutate({ id: device.id, command: "stop" })
              }
              disabled={commandMutation.isPending}
              className="text-red-11"
            >
              <Square className="size-3.5" />
              Stop
            </Button>
          </div>

          {/* Speed */}
          <div>
            <div className="mb-1 text-xs text-olive-11">Speed</div>
            <ClickToEditNumeric
              value={device.state.speed ?? null}
              unit="rpm"
              placeholder="Set speed"
              onCommit={(speed) =>
                commandMutation.mutate({
                  id: device.id,
                  command: "set_speed",
                  params: { speed },
                })
              }
            />
          </div>

          {/* Info */}
          {device.state.tubing_diameter != null && (
            <div>
              <div className="mb-1 text-xs text-olive-11">Tubing diameter</div>
              <span className="font-mono text-sm">
                {String(device.state.tubing_diameter)} mm
              </span>
            </div>
          )}

          {/* Presets configuration table */}
          <PresetTable
            deviceId={device.id}
            presets={presets}
            currentSpeed={device.state.speed ?? null}
            onSelectPreset={(rpm) =>
              commandMutation.mutate({
                id: device.id,
                command: "set_speed",
                params: { speed: rpm },
              })
            }
          />
        </div>
      </div>
    </div>
  );
}

function SensorGauge({ sensor }: { sensor: SensorState }) {
  const range = sensor.max - sensor.min;
  const normalLow = (sensor.min + range * 0.2 - sensor.min) / range;
  const normalHigh = (sensor.min + range * 0.8 - sensor.min) / range;

  const option: EChartsOption = {
    series: [
      {
        type: "gauge",
        min: sensor.min,
        max: sensor.max,
        splitNumber: 5,
        data: [
          {
            value: sensor.temp ?? 0,
            name: sensor.name,
          },
        ],
        title: {
          fontSize: 12,
          offsetCenter: [0, "85%"],
          color: "#8b8d86",
        },
        detail: {
          formatter:
            sensor.temp != null
              ? (value: number) => `${value.toFixed(1)} °C`
              : "—",
          fontSize: 16,
          fontFamily: "JetBrains Mono, monospace",
          offsetCenter: [0, "65%"],
          color: "#000000",
        },
        pointer: {
          length: "55%",
          width: 4,
          itemStyle: { color: "#c4c7bc" },
        },
        axisLine: {
          lineStyle: {
            width: 8,
            color: [
              [normalLow, "#67e8f9"],
              [normalHigh, "#4ade80"],
              [1, "#f87171"],
            ],
          },
        },
        axisTick: { length: 6, lineStyle: { color: "#687060" } },
        splitLine: { length: 12, lineStyle: { color: "#687060" } },
        axisLabel: {
          fontSize: 10,
          color: "#8b8d86",
          fontFamily: "JetBrains Mono, monospace",
        },
      },
    ],
  };

  return (
    <div className="flex flex-col items-center">
      <ReactECharts
        option={option}
        style={{ width: 220, height: 200 }}
        opts={{ renderer: "svg" }}
      />
      {sensor.error && (
        <span className="text-xs text-red-11">{sensor.error}</span>
      )}
    </div>
  );
}

function SensorConfig({
  sensorId,
  sensor,
  deviceId,
}: {
  sensorId: string;
  sensor: SensorState;
  deviceId: string;
}) {
  const utils = trpc.useUtils();

  const commandMutation = trpc.devices.command.useMutation({
    onSuccess: () => utils.devices.list.invalidate(),
    onError: (err) => toast.error(err.message),
  });

  function configure(params: Record<string, unknown>) {
    commandMutation.mutate({
      id: deviceId,
      command: "configure_sensor",
      params: { sensor_id: sensorId, ...params },
    });
  }

  return (
    <div className="flex items-center gap-4 text-sm">
      <ClickToEditText
        value={sensor.name}
        onCommit={(name) => configure({ name })}
        className="min-w-[80px]"
        inputClassName="h-6 w-32 text-sm"
      />
      <div className="flex items-center gap-1 text-xs text-olive-11">
        <span>min</span>
        <ClickToEditNumeric
          value={sensor.min}
          onCommit={(min) => configure({ min })}
          unit="°C"
        />
      </div>
      <div className="flex items-center gap-1 text-xs text-olive-11">
        <span>max</span>
        <ClickToEditNumeric
          value={sensor.max}
          onCommit={(max) => configure({ max })}
          unit="°C"
        />
      </div>
    </div>
  );
}

function RtdNodeDetail({ device }: { device: GatewayDevice }) {
  const utils = trpc.useUtils();
  const sensors = device.state.sensors ?? {};
  const sensorEntries = Object.entries(sensors).sort(([a], [b]) =>
    a.localeCompare(b),
  );

  const updateMutation = trpc.devices.update.useMutation({
    onSuccess: () => utils.devices.list.invalidate(),
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-olive-6 px-4 py-3">
        <div
          className={cn(
            "size-2.5 rounded-full",
            device.online ? "bg-green-9" : "bg-red-9",
          )}
        />
        <div className="min-w-0 flex-1">
          <ClickToEditText
            value={device.name}
            onCommit={(name) => updateMutation.mutate({ id: device.id, name })}
            className="text-base font-semibold"
            inputClassName="h-7 w-60 text-base font-semibold"
          />
          <div className="flex items-center gap-2 text-xs text-olive-11">
            <span className="font-mono">{device.address}</span>
            <span>{device.online ? "Online" : "Offline"}</span>
          </div>
        </div>
      </div>

      {/* Gauges */}
      <div className="flex-1 p-4">
        <div className="flex flex-wrap gap-4">
          {sensorEntries.map(([id, sensor]) => (
            <SensorGauge key={id} sensor={sensor} />
          ))}
        </div>

        {/* Sensor configuration */}
        <div className="mt-6 flex flex-col gap-3">
          <div className="text-xs text-olive-11">Sensor configuration</div>
          {sensorEntries.map(([id, sensor]) => (
            <SensorConfig
              key={id}
              sensorId={id}
              sensor={sensor}
              deviceId={device.id}
            />
          ))}
        </div>

        {/* Temperature history chart */}
        <DeviceReadingsChart deviceId={device.id} />

        {/* Events log */}
        <DeviceEventsLog deviceId={device.id} />
      </div>
    </div>
  );
}

function DeviceReadingsChart({ deviceId }: { deviceId: string }) {
  const readingsQuery = trpc.devices.readings.useQuery(
    { deviceId, limit: 5000 },
    { refetchInterval: 30_000 },
  );

  const readings = readingsQuery.data ?? [];

  if (readings.length === 0) {
    return (
      <div className="mt-6">
        <div className="text-xs text-olive-11">Temperature history</div>
        <div className="mt-1 text-xs text-olive-9">No data yet</div>
      </div>
    );
  }

  // Group by metric for multi-series
  const byMetric: Record<string, [number, number][]> = {};
  for (const r of readings) {
    const t = new Date(r.recorded_at).getTime();
    (byMetric[r.metric] ??= []).push([t, r.value]);
  }

  const option: EChartsOption = {
    tooltip: {
      trigger: "axis",
      valueFormatter: (v: number) => `${v.toFixed(1)} °C`,
    },
    grid: { left: 48, right: 16, top: 16, bottom: 32 },
    xAxis: {
      type: "time",
      axisLabel: { fontSize: 10, color: "#8b8d86" },
      axisLine: { lineStyle: { color: "#687060" } },
    },
    yAxis: {
      type: "value",
      name: "°C",
      nameTextStyle: { fontSize: 10, color: "#8b8d86" },
      axisLabel: {
        fontSize: 10,
        color: "#8b8d86",
        fontFamily: "JetBrains Mono, monospace",
      },
      splitLine: { lineStyle: { color: "#2e312b" } },
    },
    series: Object.entries(byMetric).map(([metric, data]) => ({
      name: metric,
      type: "line" as const,
      data,
      symbol: "none",
      sampling: "lttb" as const,
      lineStyle: { width: 1.5 },
    })),
    dataZoom: [{ type: "inside" }],
  };

  return (
    <div className="mt-6">
      <div className="text-xs text-olive-11">Temperature history</div>
      <ReactECharts
        option={option}
        style={{ height: 240 }}
        opts={{ renderer: "svg" }}
      />
    </div>
  );
}

function DeviceEventsLog({ deviceId }: { deviceId: string }) {
  const eventsQuery = trpc.devices.events.useQuery(
    { deviceId, limit: 50 },
    { refetchInterval: 30_000 },
  );

  const events = eventsQuery.data ?? [];

  if (events.length === 0) return null;

  return (
    <div className="mt-6">
      <div className="mb-1 text-xs text-olive-11">Recent events</div>
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="text-left text-olive-11">
            <th className="border-b border-olive-6 px-2 py-1 font-normal">
              Time
            </th>
            <th className="border-b border-olive-6 px-2 py-1 font-normal">
              Event
            </th>
            <th className="border-b border-olive-6 px-2 py-1 font-normal">
              Details
            </th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <tr key={event.id} className="border-b border-olive-4">
              <td className="px-2 py-1 font-mono text-olive-11">
                {new Date(event.created_at).toLocaleString("en-GB", {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </td>
              <td className="px-2 py-1">{event.event_type}</td>
              <td className="px-2 py-1 text-olive-11">
                {event.payload
                  ? formatEventPayload(event.payload as Record<string, unknown>)
                  : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ------------------------------------------------------------------ //
// Air Sensor                                                           //
// ------------------------------------------------------------------ //

const AIR_STATUS_STYLES: Record<
  string,
  { label: string; text: string; dot: string; banner: string }
> = {
  OK: {
    label: "All good",
    text: "text-green-11",
    dot: "bg-green-9",
    banner: "border-green-6 bg-green-2",
  },
  ALERT: {
    label: "Alert",
    text: "text-red-7",
    dot: "bg-red-9 animate-pulse",
    banner: "border-red-7 bg-red-3",
  },
  SNOOZED: {
    label: "Snoozed",
    text: "text-amber-11",
    dot: "bg-amber-9",
    banner: "border-amber-6 bg-amber-2",
  },
  UNAVAILABLE: {
    label: "Sensor offline",
    text: "text-yellow-11",
    dot: "bg-yellow-9",
    banner: "border-yellow-6 bg-yellow-2",
  },
};

const SNOOZE_MINUTES = 30;

function AirSensorAlarmControl({ device }: { device: GatewayDevice }) {
  const utils = trpc.useUtils();

  const commandMutation = trpc.devices.command.useMutation({
    onSuccess: () => utils.devices.list.invalidate(),
    onError: (err) => toast.error(err.message),
  });

  // Verdict rides along in the 3s devices.list poll (gateway attaches it).
  const health = device.state.health;
  const status = health?.status ?? "OK";
  const style = AIR_STATUS_STYLES[status] ?? AIR_STATUS_STYLES.OK;

  const snoozeUntil = device.state.snooze_until ?? null;
  const untilLabel = snoozeUntil
    ? new Date(snoozeUntil).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div
      className={cn(
        "mb-4 flex items-center justify-between gap-3 border px-3 py-2",
        style.banner,
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <div className={cn("size-2.5 rounded-full", style.dot)} />
        <span className={cn("text-sm font-medium", style.text)}>
          {style.label}
        </span>
        {status === "SNOOZED" && untilLabel && (
          <span className="text-xs text-olive-11">until {untilLabel}</span>
        )}
        {status === "ALERT" && health?.message && (
          <span className="truncate text-xs text-olive-11">
            {health.message}
          </span>
        )}
      </div>
      {status === "SNOOZED" ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            commandMutation.mutate({ id: device.id, command: "unsnooze" })
          }
          disabled={commandMutation.isPending}
          className="text-olive-11"
        >
          <Bell className="size-3.5" />
          Unsnooze
        </Button>
      ) : status === "ALERT" ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            commandMutation.mutate({
              id: device.id,
              command: "snooze",
              params: { minutes: SNOOZE_MINUTES },
            })
          }
          disabled={commandMutation.isPending}
          className="text-amber-11"
        >
          <BellOff className="size-3.5" />
          Snooze {SNOOZE_MINUTES}m
        </Button>
      ) : null}
    </div>
  );
}

function AirSensorDetail({ device }: { device: GatewayDevice }) {
  const utils = trpc.useUtils();

  const updateMutation = trpc.devices.update.useMutation({
    onSuccess: () => utils.devices.list.invalidate(),
    onError: (err) => toast.error(err.message),
  });

  const readings = device.state.readings ?? {};
  const lastTick = device.state.last_tick;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-olive-6 px-4 py-3">
        <div
          className={cn(
            "size-2.5 rounded-full",
            device.online ? "bg-green-9" : "bg-red-9",
          )}
        />
        <div className="min-w-0 flex-1">
          <ClickToEditText
            value={device.name}
            onCommit={(name) => updateMutation.mutate({ id: device.id, name })}
            className="text-base font-semibold"
            inputClassName="h-7 w-60 text-base font-semibold"
          />
          <div className="flex items-center gap-2 text-xs text-olive-11">
            <span className="font-mono">{device.address}</span>
            <span>{device.online ? "Online" : "Offline"}</span>
            {lastTick && (
              <span>
                {new Date(lastTick).toLocaleTimeString("en-GB", {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </span>
            )}
          </div>
        </div>
        <AirSensorExportButton deviceId={device.id} deviceName={device.name} />
      </div>

      {/* Error banner */}
      {device.state.error && (
        <div className="border-b border-red-6 px-4 py-2 text-xs text-red-11">
          {device.state.error}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        <AirSensorAlarmControl device={device} />
        <AirSensorMetricGrid readings={readings} />
        <AirSensorHistorySection deviceId={device.id} />
      </div>
    </div>
  );
}

function AirSensorMetricGrid({
  readings,
}: {
  readings: Record<string, AirSensorReading>;
}) {
  return (
    <div className="grid grid-cols-2 gap-px border border-olive-6 bg-olive-6">
      {AIR_SENSOR_METRICS.map((m) => (
        <AirSensorMetricTile key={m.key} config={m} reading={readings[m.key]} />
      ))}
    </div>
  );
}

function AirSensorMetricTile({
  config,
  reading,
}: {
  config: (typeof AIR_SENSOR_METRICS)[number];
  reading: AirSensorReading | undefined;
}) {
  const hasValue = reading?.value != null;
  const hasError = reading?.error != null && reading.error !== "";

  return (
    <div className="flex flex-col gap-1 bg-olive-1 p-3">
      <div className="text-xs text-olive-11">{config.label}</div>
      <div className="flex items-baseline gap-1">
        {hasError ? (
          <span className="text-xs text-red-11">{reading!.error}</span>
        ) : hasValue ? (
          <>
            <span className="font-mono text-base text-olive-12">
              {Number(reading!.value).toFixed(config.decimals)}
            </span>
            <span className="text-xs text-olive-10">{config.unit}</span>
          </>
        ) : (
          <span className="font-mono text-base text-olive-9">—</span>
        )}
      </div>
      {reading?.last_changed && (
        <div className="text-xs text-olive-9">
          {new Date(reading.last_changed).toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}
        </div>
      )}
    </div>
  );
}

function AirSensorHistorySection({ deviceId }: { deviceId: string }) {
  const [selectedMetric, setSelectedMetric] = useState<string>("co2");
  const [range, setRange] = useState<RangeKey>("7d");

  const selectedConfig =
    AIR_SENSOR_METRICS.find((m) => m.key === selectedMetric) ??
    AIR_SENSOR_METRICS[2];

  // CO2 has no long-term statistics in HA — it's raw-only, capped at 7 days.
  const supportsLongRange = selectedMetric !== "co2";

  const selectMetric = (key: string) => {
    setSelectedMetric(key);
    // Switching to CO2 from a longer range would query data that doesn't
    // exist — snap back to the only window CO2 supports.
    if (key === "co2") setRange("7d");
  };

  const rangeOptions = AIR_HISTORY_RANGES.map((r) => ({
    label: r.label,
    value: r.key,
    disabled: !supportsLongRange && r.key !== "7d",
    description:
      !supportsLongRange && r.key !== "7d"
        ? "No long-term data for CO2"
        : undefined,
  }));

  // Rolling window ending now, sized by the selected range.
  const hours =
    AIR_HISTORY_RANGES.find((r) => r.key === range)?.hours ?? 7 * 24;
  const endMs = Date.now();
  const startMs = endMs - hours * 60 * 60 * 1000;

  return (
    <div className="mt-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-olive-11">History</div>
        <div className="flex gap-px border border-olive-6 bg-olive-6">
          {AIR_SENSOR_METRICS.map((m) => (
            <button
              key={m.key}
              onClick={() => selectMetric(m.key)}
              className={cn(
                "px-2.5 py-1 text-xs",
                selectedMetric === m.key
                  ? "bg-[--farmless-green] text-white"
                  : "bg-olive-1 text-olive-11 hover:bg-olive-3",
              )}
            >
              {m.shortLabel}
            </button>
          ))}
        </div>
      </div>
      <div className="mb-3 flex justify-end">
        <div className="w-44">
          <SimpleSelect
            options={rangeOptions}
            value={range}
            onChange={(val) => setRange(val as RangeKey)}
            triggerClassName="h-7 text-xs"
            itemClassName="text-xs"
          />
        </div>
      </div>
      <AirSensorChart
        deviceId={deviceId}
        metric={selectedConfig.key}
        unit={selectedConfig.unit}
        decimals={selectedConfig.decimals}
        hours={hours}
        startMs={startMs}
        endMs={endMs}
      />
    </div>
  );
}

const AIR_CHART_COLOR = "#6ee7b7";

function AirSensorChart({
  deviceId,
  metric,
  unit,
  decimals,
  hours,
  startMs,
  endMs,
}: {
  deviceId: string;
  metric: string;
  unit: string;
  decimals: number;
  hours: number;
  startMs: number;
  endMs: number;
}) {
  const historyQuery = trpc.devices.airSensorHistory.useQuery(
    { deviceId, metric, hours },
    { refetchInterval: 60_000 },
  );

  const points = historyQuery.data ?? [];
  const hasRealData = points.some((p) => p.value !== null);

  if (points.length === 0) {
    return (
      <div className="mt-1 text-xs text-olive-9">
        {historyQuery.isLoading ? "Loading..." : "No data yet"}
      </div>
    );
  }

  const windowMs = endMs - startMs;
  const spanDays = windowMs / (24 * 60 * 60 * 1000);
  const data = points.map((p) => [
    new Date(p.recorded_at).getTime(),
    p.value ?? null,
  ]);

  const option: EChartsOption = {
    tooltip: {
      trigger: "axis",
      backgroundColor: "#161815",
      borderColor: "#2e312b",
      padding: [6, 10],
      textStyle: {
        color: "#c4c7bc",
        fontSize: 11,
        fontFamily: "JetBrains Mono, monospace",
      },
      valueFormatter: (v: unknown) =>
        v == null ? "Offline" : `${Number(v).toFixed(decimals)} ${unit}`,
    },
    grid: { left: 52, right: 16, top: 16, bottom: 52 },
    xAxis: {
      type: "time",
      min: startMs,
      max: endMs,
      minInterval: spanDays <= 2 ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000,
      axisLabel: {
        fontSize: 10,
        color: "#8b8d86",
        formatter: (value: number) => {
          const d = new Date(value);
          // ≤2 days: time of day. ≤~3 months: day + month. Longer: month + year.
          if (spanDays <= 2) {
            return d.toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
            });
          }
          if (spanDays <= 92) {
            const today = new Date();
            const isToday =
              d.getDate() === today.getDate() &&
              d.getMonth() === today.getMonth() &&
              d.getFullYear() === today.getFullYear();
            if (isToday) return "Today";
            return d.toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
            });
          }
          return d.toLocaleDateString("en-GB", {
            month: "short",
            year: "2-digit",
          });
        },
      },
      axisLine: { lineStyle: { color: "#2e312b" } },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      axisLabel: {
        fontSize: 10,
        color: "#8b8d86",
        fontFamily: "JetBrains Mono, monospace",
        formatter: (v: number) => Number(v).toFixed(decimals),
      },
      splitLine: { lineStyle: { color: "#1e211b", type: "dashed" as const } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        type: "line" as const,
        data,
        symbol: "none",
        sampling: "lttb" as const,
        connectNulls: false,
        lineStyle: { width: 1.5, color: AIR_CHART_COLOR },
        itemStyle: { color: AIR_CHART_COLOR },
        areaStyle: {
          color: {
            type: "linear" as const,
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: "rgba(110, 231, 183, 0.18)" },
              { offset: 1, color: "rgba(110, 231, 183, 0.01)" },
            ],
          },
        },
      },
    ],
    dataZoom: [
      { type: "inside" },
      {
        type: "slider",
        height: 18,
        bottom: 6,
        borderColor: "#2e312b",
        backgroundColor: "#0d0f0c",
        fillerColor: "rgba(110, 231, 183, 0.1)",
        handleStyle: { color: "#3d6b52", borderColor: "#3d6b52" },
        moveHandleStyle: { color: "#3d6b52" },
        textStyle: { color: "#8b8d86", fontSize: 9 },
      },
    ],
  };

  if (!hasRealData) {
    return (
      <div className="mt-1 text-xs text-olive-9">
        No readings in selected range
      </div>
    );
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: 280 }}
      opts={{ renderer: "svg" }}
    />
  );
}

function formatEventPayload(payload: Record<string, unknown>): string {
  if (payload.command) {
    const parts = [String(payload.command)];
    if (payload.params && typeof payload.params === "object") {
      const p = payload.params as Record<string, unknown>;
      const paramStr = Object.entries(p)
        .map(([k, v]) => `${k}=${v}`)
        .join(", ");
      if (paramStr) parts.push(paramStr);
    }
    return parts.join(" ");
  }
  return Object.entries(payload)
    .map(([k, v]) => `${k}=${v}`)
    .join(", ");
}

function PresetTable({
  deviceId,
  presets,
  currentSpeed,
  onSelectPreset,
}: {
  deviceId: string;
  presets: Array<{ rpm: number; flow_rate: number }>;
  currentSpeed: number | null;
  onSelectPreset: (rpm: number) => void;
}) {
  const utils = trpc.useUtils();

  const presetsMutation = trpc.devices.updatePresets.useMutation({
    onSuccess: () => utils.devices.list.invalidate(),
    onError: (err) => toast.error(err.message),
  });

  function savePresets(updated: Array<{ rpm: number; flow_rate: number }>) {
    presetsMutation.mutate({ id: deviceId, presets: updated });
  }

  function handleAdd() {
    savePresets([...presets, { rpm: 0, flow_rate: 0 }]);
  }

  function handleDelete(index: number) {
    savePresets(presets.filter((_, i) => i !== index));
  }

  function handleChange(
    index: number,
    field: "rpm" | "flow_rate",
    value: number,
  ) {
    const updated = presets.map((p, i) =>
      i === index ? { ...p, [field]: value } : p,
    );
    savePresets(updated);
  }

  return (
    <div>
      <div className="mb-1 text-xs text-olive-11">Flow rate presets</div>
      {presets.length > 0 && (
        <table className="border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs text-olive-11">
              <th className="border-b border-olive-6 px-2 py-1 font-normal">
                RPM
              </th>
              <th className="border-b border-olive-6 px-2 py-1 font-normal">
                mL/min
              </th>
              <th className="border-b border-olive-6 px-2 py-1" />
              <th className="w-6 border-b border-olive-6" />
            </tr>
          </thead>
          <tbody>
            {presets.map((preset, index) => {
              const isActive =
                currentSpeed != null &&
                Math.abs(currentSpeed - preset.rpm) < 0.01;
              return (
                <PresetRow
                  key={index}
                  preset={preset}
                  isActive={isActive}
                  onChange={(field, value) => handleChange(index, field, value)}
                  onDelete={() => handleDelete(index)}
                  onSelect={() => onSelectPreset(preset.rpm)}
                />
              );
            })}
          </tbody>
        </table>
      )}
      <button
        onClick={handleAdd}
        className="mt-1 flex items-center gap-1 text-xs text-olive-11 hover:text-olive-12"
      >
        <Plus className="size-3" />
        Add preset
      </button>
    </div>
  );
}

function PresetRow({
  preset,
  isActive,
  onChange,
  onDelete,
  onSelect,
}: {
  preset: { rpm: number; flow_rate: number };
  isActive: boolean;
  onChange: (field: "rpm" | "flow_rate", value: number) => void;
  onDelete: () => void;
  onSelect: () => void;
}) {
  const [editingField, setEditingField] = useState<"rpm" | "flow_rate" | null>(
    null,
  );
  const [draft, setDraft] = useState<number | null>(null);

  function startEdit(field: "rpm" | "flow_rate") {
    setEditingField(field);
    setDraft(preset[field]);
  }

  function commitEdit() {
    if (editingField && draft != null) {
      onChange(editingField, draft);
    }
    setEditingField(null);
    setDraft(null);
  }

  function cancelEdit() {
    setEditingField(null);
    setDraft(null);
  }

  return (
    <tr
      className={cn("group border-b border-olive-4", isActive && "bg-olive-3")}
    >
      <td className="px-2 py-0.5">
        {editingField === "rpm" ? (
          <NumericInput
            value={draft}
            onValueChange={(v) => setDraft(v ?? null)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter") commitEdit();
              if (e.key === "Escape") cancelEdit();
            }}
            onBlur={commitEdit}
            className="h-6 w-16 font-mono text-sm"
            autoFocus
          />
        ) : (
          <button
            className="font-mono text-sm hover:underline"
            onClick={() => startEdit("rpm")}
          >
            {String(preset.rpm)}
          </button>
        )}
      </td>
      <td className="px-2 py-0.5">
        {editingField === "flow_rate" ? (
          <NumericInput
            value={draft}
            onValueChange={(v) => setDraft(v ?? null)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter") commitEdit();
              if (e.key === "Escape") cancelEdit();
            }}
            onBlur={commitEdit}
            className="h-6 w-16 font-mono text-sm"
            autoFocus
          />
        ) : (
          <button
            className="font-mono text-sm hover:underline"
            onClick={() => startEdit("flow_rate")}
          >
            {String(preset.flow_rate)}
          </button>
        )}
      </td>
      <td className="px-2 py-0.5">
        <button
          className={cn(
            "flex items-center gap-1 text-xs opacity-0 group-hover:opacity-100",
            isActive ? "text-green-11" : "text-olive-11 hover:text-olive-12",
          )}
          onClick={onSelect}
          title="Set this speed"
        >
          <Zap className="size-3" />
          Use
        </button>
      </td>
      <td className="py-0.5 opacity-0 group-hover:opacity-100">
        <DeleteConfirmDialog
          description={`Remove the ${preset.flow_rate} mL/min preset?`}
          onConfirm={onDelete}
          triggerSize="sm"
        />
      </td>
    </tr>
  );
}
